import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileSearch, RefreshCw } from 'lucide-react';
import { toast } from '../../lib/safeToast';

const REC_BADGE = {
  approve: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  reject: 'bg-red-50 text-red-700 border-red-200',
  request_info: 'bg-amber-50 text-amber-700 border-amber-200',
};

const DECISION_BADGE = {
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const FRIENDLY_LABELS = {
  co_recommended_approve: 'Compliance Approved',
  co_recommended_reject: 'Compliance Rejected',
  pending_su_approval: 'Awaiting SU Decision',
  pending_su_review: 'Awaiting SU Decision',
};
const label = (s) =>
  FRIENDLY_LABELS[s] || (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function truncate(str, len = 60) {
  if (!str) return '—';
  return str.length > len ? str.slice(0, len) + '…' : str;
}

export default function BackOfficeComplianceReviews() {
  const { api } = useAuth();
  const { isLive } = useEnvironment();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/kyc/reviews');
      setRows(res.data?.data || []);
    } catch {
      setRows([]);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews, isLive]);

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
              <FileSearch className="w-7 h-7 text-[#0B6445]" />
              All CO Reviews
            </h1>
            <p className="text-[#0E1B16]/70 mt-2">Compliance officer review history and recommendations.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReviews}
            className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner w-8 h-8" />
              </div>
            ) : rows.length === 0 ? (
              <p className="text-center py-12 text-[#0E1B16]/60">No reviews found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#DCE4E0]">
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Merchant ID</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Reviewer</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Recommendation</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Comments</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">SU Decision</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={row.id || i} className="border-b border-[#DCE4E0]/80 hover:bg-[#F8FAFC]">
                        <td className="py-3 px-2 font-mono text-xs">{row.merchant_id || row.user_id || '—'}</td>
                        <td className="py-3 px-2">{row.reviewer_name || row.reviewer_email || '—'}</td>
                        <td className="py-3 px-2">
                          <Badge
                            variant="outline"
                            className={REC_BADGE[row.recommendation] || 'bg-slate-50 text-slate-600 border-slate-200'}
                          >
                            {label(row.recommendation)}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 max-w-[200px]" title={row.comments || ''}>
                          {truncate(row.comments)}
                        </td>
                        <td className="py-3 px-2">
                          {row.su_decision ? (
                            <Badge
                              variant="outline"
                              className={DECISION_BADGE[row.su_decision] || 'bg-slate-50 text-slate-600 border-slate-200'}
                            >
                              {label(row.su_decision)}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">
                              Pending SU
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
}
